import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Column, DataTable } from "@/components/ui/data-table";
import { formatDateTime } from "@/utils/dateUtils";
import type {
  PartnerOrdersImportHistoryEntry,
  PartnerOrdersImportSkippedRow,
} from "@/types/partner-order";
import { describeError } from "../utils/describeError";
import {
  useImportOrdersReport,
  useOrdersImportHistory,
} from "../hooks/admin-hotel-partner-sync.hooks";

const MAX_IMPORT_FILE_BYTES = 10 * 1024 * 1024;

function renderHistoryBody({
  isHistoryLoading,
  isHistoryError,
  history,
  historyColumns,
  t,
}: {
  isHistoryLoading: boolean;
  isHistoryError: boolean;
  history: PartnerOrdersImportHistoryEntry[] | undefined;
  historyColumns: Column<PartnerOrdersImportHistoryEntry>[];
  t: (key: string) => string;
}): React.JSX.Element {
  if (isHistoryLoading) {
    return (
      <p className="text-muted-foreground text-sm">
        {t("partners.admin.import.historyLoading")}
      </p>
    );
  }
  if (isHistoryError) {
    return (
      <p className="text-destructive text-sm">
        {t("partners.admin.import.historyLoadError")}
      </p>
    );
  }
  return (
    <DataTable
      data={history ?? []}
      columns={historyColumns}
      emptyMessage={t("partners.admin.import.historyEmpty")}
      getRowKey={(row) => row.id}
      withPagination={false}
      withSorting={false}
    />
  );
}

/**
 * Task 0033, contract 2026-09-29: Viator provider page, Guesteria staff
 * only — upload a booking-report CSV, see the result (created/updated/
 * skipped with reasons), and the import history (manual + the daily robot
 * run).
 */
export const PartnerOrdersImportPanel: React.FC<{ canWrite: boolean }> = ({
  canWrite,
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);

  const { importReport, isImporting, result, resetResult } =
    useImportOrdersReport();
  const {
    data: history,
    isLoading: isHistoryLoading,
    isError: isHistoryError,
  } = useOrdersImportHistory(20);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    resetResult();
    if (file && file.size > MAX_IMPORT_FILE_BYTES) {
      setSelectedFile(null);
      setFileError(t("partners.admin.import.fileTooLarge"));
      return;
    }
    setSelectedFile(file);
    setFileError(null);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setFileError(t("partners.admin.import.noFileSelected"));
      return;
    }
    try {
      await importReport(selectedFile);
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (error) {
      // 400 (and any other failure) — a fixed translated message, the hook's
      // own onError already toasts it; nothing further to do here.
      toast.error(describeError(t("partners.admin.import.error"), error));
    }
  };

  const skippedColumns: Column<PartnerOrdersImportSkippedRow>[] = [
    { key: "ref", header: t("partners.admin.import.skippedColumns.ref") },
    { key: "line", header: t("partners.admin.import.skippedColumns.line") },
    {
      key: "reason",
      header: t("partners.admin.import.skippedColumns.reason"),
      className: "min-w-[220px] whitespace-normal",
    },
  ];

  const historyColumns: Column<PartnerOrdersImportHistoryEntry>[] = [
    {
      key: "source",
      header: t("partners.admin.import.historyColumns.source"),
      render: (value: "upload" | "robot") => (
        <Badge variant={value === "robot" ? "secondary" : "outline"}>
          {t(`partners.admin.import.sourceValues.${value}`)}
        </Badge>
      ),
    },
    {
      key: "fileName",
      header: t("partners.admin.import.historyColumns.fileName"),
      render: (value: string | null) => value || "—",
    },
    {
      key: "importedByEmail",
      header: t("partners.admin.import.historyColumns.importedBy"),
      render: (value: string | null) => value || "—",
    },
    { key: "rows", header: t("partners.admin.import.historyColumns.rows") },
    {
      key: "created",
      header: t("partners.admin.import.historyColumns.created"),
    },
    {
      key: "updated",
      header: t("partners.admin.import.historyColumns.updated"),
    },
    {
      key: "skipped",
      header: t("partners.admin.import.historyColumns.skipped"),
    },
    {
      key: "createdAt",
      header: t("partners.admin.import.historyColumns.createdAt"),
      render: (value: string) => formatDateTime(value),
    },
  ];

  return (
    <div className="flex flex-col gap-6 rounded-xl border p-4">
      <div className="flex flex-col gap-1">
        <h2 className="text-foreground text-lg font-semibold">
          {t("partners.admin.import.title")}
        </h2>
        <p className="text-muted-foreground text-sm">
          {t("partners.admin.import.description")}
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,text/csv"
          disabled={!canWrite || isImporting}
          onChange={handleFileChange}
          className="text-sm"
        />
        <Button
          type="button"
          disabled={!canWrite || isImporting || !selectedFile}
          onClick={handleUpload}
        >
          {isImporting
            ? t("partners.admin.import.uploading")
            : t("partners.admin.import.upload")}
        </Button>
      </div>
      {fileError && <p className="text-destructive text-xs">{fileError}</p>}

      {result && (
        <div className="flex flex-col gap-2 rounded-lg border p-3">
          <h3 className="text-foreground text-sm font-semibold">
            {t("partners.admin.import.resultTitle")}
          </h3>
          <p className="text-muted-foreground text-sm">
            {t("partners.admin.import.resultSummary", {
              rows: result.rows,
              created: result.created,
              updated: result.updated,
              skippedCount: result.skipped.length,
            })}
          </p>
          <div className="flex flex-col gap-2">
            <span className="text-foreground text-xs font-medium">
              {t("partners.admin.import.skippedTitle")}
            </span>
            {result.skipped.length === 0 ? (
              <p className="text-muted-foreground text-sm">
                {t("partners.admin.import.skippedEmpty")}
              </p>
            ) : (
              <DataTable
                data={result.skipped}
                columns={skippedColumns}
                getRowKey={(row, index) => `${row.ref}-${index}`}
                withPagination={false}
                withSorting={false}
              />
            )}
          </div>
        </div>
      )}

      <div className="flex flex-col gap-2">
        <h3 className="text-foreground text-sm font-semibold">
          {t("partners.admin.import.historyTitle")}
        </h3>
        {renderHistoryBody({
          isHistoryLoading,
          isHistoryError,
          history,
          historyColumns,
          t,
        })}
      </div>
    </div>
  );
};
